<template>
  <div class="animated fadeIn">

    <b-row class="mb-3 align-items-center">
      <b-col>
        <h4 class="mb-0"><i class="icon-plus mr-2 text-danger"></i>Create New Issue</h4>
      </b-col>
      <b-col class="text-right">
        <b-button variant="outline-secondary" size="sm" to="/issue-tracker/list">
          <i class="icon-arrow-left mr-1"></i>Back to List
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <!-- Form -->
      <b-col md="8" class="mb-4">
        <b-card class="shadow-sm">
          <b-form @submit.prevent="submit">

            <b-form-group label="Issue Title *" label-class="fw-600">
              <b-form-input v-model.trim="form.title" placeholder="Short, clear description of the issue" required />
            </b-form-group>

            <b-form-row>
              <b-col md="6">
                <b-form-group label="Client Type *" label-class="fw-600">
                  <b-form-select v-model="form.clientType" :options="clientTypeOptions" required />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Category *" label-class="fw-600">
                  <b-form-select v-model="form.category" :options="categoryOptions" required />
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-row>
              <b-col md="6">
                <b-form-group label="Client ID" description="Office ID, Project ID, or reference">
                  <b-form-input v-model.trim="form.clientId" placeholder="e.g. OFFICE-101" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Client Name" description="Company or office name">
                  <b-form-input v-model.trim="form.clientName" placeholder="e.g. ABC Corporation" />
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-row>
              <b-col md="6">
                <b-form-group label="Priority *" label-class="fw-600">
                  <b-form-select v-model="form.priority" :options="priorityOptions" required />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Assign To" description="Select a support team member">
                  <b-form-select v-model="form.assignedMemberId" :options="memberOptions">
                    <template #first>
                      <b-form-select-option :value="null">— Unassigned —</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-col>
            </b-form-row>

            <b-form-group label="Due Date" description="Optional deadline">
              <b-form-input v-model="form.dueDate" type="datetime-local" />
            </b-form-group>

            <b-form-group label="Description" description="Reproduce steps, environment, screenshots info…">
              <b-form-textarea v-model="form.description" rows="5"
                               placeholder="Describe the issue in detail…" />
            </b-form-group>

            <div class="d-flex mt-3">
              <b-button type="submit" variant="danger" :disabled="submitting" class="mr-2 px-4">
                <b-spinner v-if="submitting" small class="mr-1" />
                {{ submitting ? 'Creating…' : 'Create Issue' }}
              </b-button>
              <b-button type="button" variant="outline-secondary" to="/issue-tracker/list">
                Cancel
              </b-button>
            </div>
          </b-form>
        </b-card>
      </b-col>

      <!-- Right Guide -->
      <b-col md="4" class="mb-4">
        <b-card header="Priority Guide" class="shadow-sm mb-3">
          <b-list-group flush>
            <b-list-group-item class="px-0">
              <div class="d-flex align-items-start">
                <b-badge variant="danger" class="mr-2 mt-1" style="min-width:70px;text-align:center">CRITICAL</b-badge>
                <small class="text-muted">System down, data loss, affecting many users. Requires immediate action.</small>
              </div>
            </b-list-group-item>
            <b-list-group-item class="px-0">
              <div class="d-flex align-items-start">
                <b-badge variant="warning" class="mr-2 mt-1" style="min-width:70px;text-align:center">HIGH</b-badge>
                <small class="text-muted">Major feature broken, blocking one client's workflow.</small>
              </div>
            </b-list-group-item>
            <b-list-group-item class="px-0">
              <div class="d-flex align-items-start">
                <b-badge variant="info" class="mr-2 mt-1" style="min-width:70px;text-align:center">MEDIUM</b-badge>
                <small class="text-muted">Feature partially working, workaround available.</small>
              </div>
            </b-list-group-item>
            <b-list-group-item class="px-0">
              <div class="d-flex align-items-start">
                <b-badge variant="secondary" class="mr-2 mt-1" style="min-width:70px;text-align:center">LOW</b-badge>
                <small class="text-muted">Minor UI issue, enhancement request, nice-to-have.</small>
              </div>
            </b-list-group-item>
          </b-list-group>
        </b-card>

        <b-card header="Client Types" class="shadow-sm">
          <b-list-group flush>
            <b-list-group-item class="px-0 d-flex align-items-center">
              <b-badge variant="primary" class="mr-2">HRM</b-badge>
              <small class="text-muted">HRM application client issues</small>
            </b-list-group-item>
            <b-list-group-item class="px-0 d-flex align-items-center">
              <b-badge variant="success" class="mr-2">CS</b-badge>
              <small class="text-muted">Customer Support system issues</small>
            </b-list-group-item>
            <b-list-group-item class="px-0 d-flex align-items-center">
              <b-badge variant="purple" class="mr-2" style="background:#6f42c1">ADMIN</b-badge>
              <small class="text-muted">Admin panel internal issues</small>
            </b-list-group-item>
            <b-list-group-item class="px-0 d-flex align-items-center">
              <b-badge variant="warning" class="mr-2">OTHER</b-badge>
              <small class="text-muted">Other platforms or external</small>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-col>
    </b-row>

  </div>
</template>

<script>
import issueService from '@/services/issueService'
import teamMemberService from '@/services/teamMemberService'

export default {
  name: 'IssueCreate',
  data: function() {
    return {
      submitting: false,
      memberOptions: [],
      form: {
        title: '', description: '', clientType: 'HRM', clientId: '',
        clientName: '', priority: 'MEDIUM', category: 'SUPPORT',
        assignedMemberId: null, dueDate: ''
      },
      clientTypeOptions: [
        { value: 'HRM',   text: 'HRM' },
        { value: 'CS',    text: 'CS' },
        { value: 'ADMIN', text: 'Admin' },
        { value: 'OTHER', text: 'Other' },
      ],
      priorityOptions: [
        { value: 'CRITICAL', text: 'Critical' },
        { value: 'HIGH',     text: 'High' },
        { value: 'MEDIUM',   text: 'Medium' },
        { value: 'LOW',      text: 'Low' },
      ],
      categoryOptions: [
        { value: 'BUG',             text: 'Bug' },
        { value: 'FEATURE_REQUEST', text: 'Feature Request' },
        { value: 'SUPPORT',         text: 'Support' },
        { value: 'BILLING',         text: 'Billing' },
        { value: 'PERFORMANCE',     text: 'Performance' },
        { value: 'OTHER',           text: 'Other' },
      ]
    }
  },
  created: function() { this.loadMembers() },
  methods: {
    loadMembers: function() {
      var self = this
      teamMemberService.listActive(
        function(data) {
          self.memberOptions = (data || []).map(function(m) {
            return { value: m.id, text: m.full_name + ' (' + m.position + ')' }
          })
        },
        function() {}
      )
    },
    submit: function() {
      if (!this.form.title || !this.form.clientType || !this.form.priority) {
        this.$bvToast.toast('Please fill in all required fields.', { title: 'Validation', variant: 'warning', solid: true })
        return
      }
      var self = this
      self.submitting = true
      issueService.create(
        self.form,
        function(data) {
          self.submitting = false
          self.$bvToast.toast('Issue #' + data.id + ' created successfully.', { title: 'Created', variant: 'success', solid: true })
          setTimeout(function() { self.$router.push('/issue-tracker/list') }, 1200)
        },
        function(err) {
          self.submitting = false
          self.$bvToast.toast(err, { title: 'Error', variant: 'danger', solid: true })
        }
      )
    }
  }
}
</script>

<style scoped>
.fw-600 { font-weight: 600; }
</style>
